import { MessageSquareDashed, RefreshCcw, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/primitives/button';
import { cn } from '@/lib/utils';
import { useAssistantStore } from '@/state/assistant-store';
import { useSessionStore } from '@/state/session-store';

type CopilotChatComponent = ComponentType<{
  className?: string;
  instructions: string;
  labels: {
    title: string;
    initial: string;
    placeholder: string;
  };
  suggestions: Array<{
    title: string;
    message: string;
  }>;
}>;

function buildSuggestions(pathname: string) {
  if (pathname.startsWith('/companies')) {
    return [
      { title: 'Summarize company context', message: 'Summarize the most important company context on this screen.' },
      { title: 'Find diligence gaps', message: 'What diligence gaps or follow-up questions should I prioritize from the available company data?' },
    ];
  }

  if (pathname.startsWith('/documents')) {
    return [
      { title: 'Document briefing', message: 'Summarize the most recent document activity and call out what needs review.' },
      { title: 'Board prep', message: 'Draft a concise board-readout based on the latest documents and activity.' },
    ];
  }

  if (pathname.startsWith('/tasks')) {
    return [
      { title: 'Task priorities', message: 'What are the highest-priority task risks in this workspace right now?' },
      { title: 'Draft follow-up task', message: 'Draft a follow-up task based on the current priorities and blockers.' },
    ];
  }

  return [
    { title: 'Executive summary', message: 'Give me an executive summary of the active organization.' },
    { title: 'Draft briefing', message: 'Draft an executive briefing from the latest work across the workspace.' },
  ];
}

export function AssistantPanel() {
  const location = useLocation();
  const runtimeInfo = useAssistantStore((state) => state.runtimeInfo);
  const assistantOpen = useAssistantStore((state) => state.assistantOpen);
  const setAssistantOpen = useAssistantStore((state) => state.setAssistantOpen);
  const resetThread = useAssistantStore((state) => state.resetThread);
  const [CopilotChat, setCopilotChat] = useState<CopilotChatComponent | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { profile, memberships, activeOrganizationId } = useSessionStore(
    useShallow((state) => ({
      profile: state.profile,
      memberships: state.memberships,
      activeOrganizationId: state.activeOrganizationId,
    })),
  );

  const activeOrganization = useMemo(
    () => memberships.find((membership) => membership.organization_id === activeOrganizationId)?.organization ?? null,
    [activeOrganizationId, memberships],
  );
  const suggestions = useMemo(() => buildSuggestions(location.pathname), [location.pathname]);

  useEffect(() => {
    if (!assistantOpen || !runtimeInfo?.enabled || !runtimeInfo.runtimeUrl || CopilotChat) {
      return;
    }

    let mounted = true;

    void (async () => {
      try {
        const [{ CopilotChat: chatComponent }] = await Promise.all([
          import('@copilotkit/react-ui'),
          import('@copilotkit/react-ui/styles.css'),
        ]);

        if (!mounted) {
          return;
        }

        setCopilotChat(() => chatComponent as CopilotChatComponent);
        setLoadError(null);
      } catch (error) {
        if (!mounted) {
          return;
        }

        setLoadError(error instanceof Error ? error.message : 'Unable to load the assistant UI.');
      }
    })();

    return () => {
      mounted = false;
    };
  }, [CopilotChat, assistantOpen, runtimeInfo?.enabled, runtimeInfo?.runtimeUrl]);

  if (!runtimeInfo?.enabled || !runtimeInfo.runtimeUrl) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          'pointer-events-none fixed inset-y-0 right-0 z-40 flex w-[min(440px,calc(100vw-2rem))] translate-x-full px-4 py-4 transition duration-200',
          assistantOpen && 'pointer-events-auto translate-x-0',
        )}
      >
        <div className="flex w-full flex-col overflow-hidden rounded-[28px] border border-white/10 bg-ink-950/98 shadow-[0_24px_80px_-28px_rgba(0,0,0,0.65)] backdrop-blur-2xl">
          <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-accent-300">Global copilot</p>
              <h2 className="mt-2 text-lg font-semibold text-white">Nordic assistant</h2>
              <p className="mt-1 text-sm text-slate-400">
                {activeOrganization?.name ?? 'No organization selected'} {runtimeInfo.model ? `• ${runtimeInfo.model}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" className="h-10 w-10 rounded-full p-0" onClick={resetThread} aria-label="Reset assistant thread">
                <RefreshCcw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" className="h-10 w-10 rounded-full p-0" onClick={() => setAssistantOpen(false)} aria-label="Close assistant">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="border-b border-white/10 bg-white/[0.03] px-5 py-3">
            <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
              <span>{profile?.full_name ?? 'Executive user'}</span>
              <span>{location.pathname.replace('/', '') || 'dashboard'}</span>
            </div>
          </div>

          <div className="nordic-copilot flex-1 overflow-hidden p-3">
            {loadError ? (
              <div className="flex h-full items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-slate-400">
                {loadError}
              </div>
            ) : CopilotChat ? (
              <CopilotChat
                className="nordic-copilot-chat h-full"
                instructions="You are NordicCompanyHub's embedded executive copilot. Use the active organization context, read available workspace data when needed, and prefer concise operational answers. When the user asks you to create work, use draft actions so the UI is prefilled, but remind yourself that final submission stays with the user."
                labels={{
                  title: 'Nordic assistant',
                  initial: `Focus on ${activeOrganization?.name ?? 'the active organization'} and help the user move work forward.`,
                  placeholder: 'Ask about risks, summaries, next steps, or draft work...',
                }}
                suggestions={suggestions}
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-sm text-slate-400">
                Loading assistant UI...
              </div>
            )}
          </div>
        </div>
      </div>

      {!assistantOpen ? (
        <button
          type="button"
          className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-3 rounded-full border border-accent-400/25 bg-ink-950/95 px-4 py-3 text-sm font-semibold text-white shadow-panel transition hover:border-accent-400/45 hover:bg-ink-900"
          onClick={() => setAssistantOpen(true)}
        >
          <MessageSquareDashed className="h-4 w-4 text-accent-300" />
          Open assistant
        </button>
      ) : null}
    </>
  );
}
